/**
 * API Retry Types
 *
 * This file contains type definitions for the retry system, including
 * retry configuration, attempt tracking, retry engine interface, and
 * retry metrics snapshots. These types are used by the Retry Engine,
 * Request Coordinator, and related resilience components.
 *
 * @module types/api/retry
 */

import type { PerformanceMetrics } from './integration.types';
import type { CoreNotificationEvent } from './notification.types';
import type { RequestContext } from './request.types';
import type { AxiosInstance, AxiosResponse } from 'axios';

// =============================================================================
// RETRY COORDINATION RESULT
// =============================================================================

/**
 * Result of a retry coordination operation.
 *
 * This type is specifically for the retry system and uses the full RequestContext
 * and PerformanceMetrics types, unlike the simplified CoordinationResult in
 * integration.types.ts which uses IntegrationRequestContext for interface purposes.
 *
 * @example
 * ```typescript
 * const result: RetryCoordinationResult = {
 *   success: true,
 *   response: axiosResponse,
 *   context: requestContext,
 *   metrics: performanceMetrics,
 * };
 * ```
 */
export interface RetryCoordinationResult {
  /** Whether the operation was successful */
  success: boolean;
  /** Axios response if successful */
  response?: AxiosResponse;
  /** Error if failed */
  error?: Error;
  /** Request context with full lifecycle information */
  context: RequestContext;
  /** Performance metrics for the operation */
  metrics: PerformanceMetrics;
}

// =============================================================================
// RETRY CONFIGURATION
// =============================================================================

/**
 * Configuration for the retry system.
 *
 * This interface defines all parameters that control retry behavior,
 * including timing, backoff strategy, and endpoint filtering.
 *
 * @example
 * ```typescript
 * const retryConfig: RetryConfig = {
 *   maxAttempts: 3,
 *   baseDelay: 1000,
 *   maxDelay: 10000,
 *   exponentialBase: 2,
 *   jitterFactor: 0.1,
 *   enableCircuitBreaker: true,
 *   retryableStatusCodes: [408, 429, 500, 502, 503, 504],
 *   retryableMethods: ['GET', 'PUT', 'HEAD', 'OPTIONS', 'DELETE'],
 *   excludeEndpoints: ['/login', '/logout', '/register'],
 * };
 * ```
 */
export interface RetryConfig {
  /**
   * Total number of attempts before giving up, INCLUDING the initial request.
   * The tracker derives `maxRetries = maxAttempts - 1`, so `maxAttempts: 3`
   * means 1 initial request + 2 retries.
   * @default 3
   */
  maxAttempts: number;

  /**
   * Base delay in milliseconds before the first retry.
   * Used as the starting point for exponential backoff.
   * @default 1000
   */
  baseDelay: number;

  /**
   * Maximum delay in milliseconds between retries.
   * Caps the exponential backoff to prevent excessive waits.
   * @default 10000
   */
  maxDelay: number;

  /**
   * Base for exponential backoff calculation.
   * Delay = baseDelay * (exponentialBase ^ attemptNumber)
   * @default 2
   */
  exponentialBase: number;

  /**
   * Factor for adding randomness to retry delays (0-1).
   * Helps prevent thundering herd when multiple clients retry simultaneously.
   * @default 0.1
   */
  jitterFactor: number;

  /**
   * Whether to enable circuit breaker integration.
   * When true, circuit breaker state is checked before retrying.
   * @default true
   */
  enableCircuitBreaker: boolean;

  /**
   * HTTP status codes that should trigger a retry.
   * @default [408, 429, 500, 502, 503, 504]
   */
  retryableStatusCodes: number[];

  /**
   * HTTP methods that are safe to retry.
   * Non-idempotent methods (POST) are typically excluded.
   * @default ['GET', 'PUT', 'HEAD', 'OPTIONS', 'DELETE']
   */
  retryableMethods: string[];

  /**
   * Endpoint patterns that should never be retried.
   * Typically includes authentication endpoints.
   * @default ['/login', '/logout', '/register', '/api/user/auth']
   */
  excludeEndpoints: string[];
}

// =============================================================================
// RETRY ATTEMPT
// =============================================================================

/**
 * Record of a single retry attempt.
 *
 * This interface captures details about each individual retry attempt,
 * useful for debugging and metrics collection.
 *
 * @example
 * ```typescript
 * const attempt: RetryAttempt = {
 *   attemptNumber: 1,
 *   delay: 1000,
 *   error: new Error('Connection timeout'),
 *   timestamp: dayjs().valueOf(),
 *   requestId: 'req_123',
 * };
 * ```
 */
export interface RetryAttempt {
  /**
   * The attempt number (1-indexed).
   * First retry is attempt 1, second is attempt 2, etc.
   */
  attemptNumber: number;

  /**
   * Delay in milliseconds before this attempt was made.
   * Includes jitter if configured.
   */
  delay: number;

  /**
   * The error that triggered this retry attempt.
   */
  error: Error;

  /**
   * Unix timestamp when this attempt was initiated.
   */
  timestamp: number;

  /**
   * The request ID this attempt belongs to.
   */
  requestId: string;
}

// =============================================================================
// RETRY METRICS SNAPSHOT
// =============================================================================

/**
 * Snapshot of retry metrics for monitoring and debugging.
 *
 * This interface provides a summary of retry activity,
 * used by the retry engine to report on its performance.
 *
 * @example
 * ```typescript
 * const metrics: RetryMetricsSnapshot = engine.getRetryMetrics();
 * console.log(`Success rate: ${metrics.retrySuccessRate}%`);
 * console.log(`Average delay: ${metrics.averageDelay}ms`);
 * ```
 */
export interface RetryMetricsSnapshot {
  /**
   * Total number of retry attempts made.
   */
  totalAttempts: number;

  /**
   * Number of retries that eventually succeeded.
   */
  successfulRetries: number;

  /**
   * Number of retries that failed after all attempts exhausted.
   */
  failedRetries: number;

  /**
   * Average delay in milliseconds across all retry attempts.
   */
  averageDelay: number;

  /**
   * Total accumulated delay from all retries in milliseconds.
   */
  totalDelay: number;

  /**
   * Success rate as a percentage (0-100).
   * Calculated as: (successfulRetries / totalAttempts) * 100
   */
  retrySuccessRate: number;

  /**
   * Timestamp of the most recent retry attempt.
   */
  lastRetryTimestamp?: number;
}

// =============================================================================
// RETRY ENGINE INTERFACE
// =============================================================================

/**
 * Interface for the retry engine component.
 *
 * The retry engine is responsible for executing retry logic,
 * calculating delays, managing metrics, and coordinating with
 * the circuit breaker system.
 *
 * @example
 * ```typescript
 * const engine: RetryEngine = createRetryEngine(config);
 *
 * // Execute a retry
 * const result = await engine.executeRetry(context, axiosClient);
 *
 * // Get metrics
 * const metrics = engine.getRetryMetrics();
 *
 * // Check if should retry
 * if (engine.shouldContinueRetrying(context)) {
 *   // Continue with retry...
 * }
 * ```
 */
export interface RetryEngine {
  /**
   * Executes retry logic for a failed request.
   * Handles backoff delays, metrics updates, and circuit breaker coordination.
   *
   * @param context - The request context containing failure information
   * @param axiosClient - The Axios instance to use for retrying
   * @returns Promise resolving to the coordination result
   */
  executeRetry(
    context: RequestContext,
    axiosClient: AxiosInstance
  ): Promise<RetryCoordinationResult>;

  /**
   * Calculates the delay before the next retry attempt.
   * Uses exponential backoff with optional jitter.
   *
   * @param attempt - The current attempt number (0-indexed)
   * @param config - The retry configuration
   * @returns Delay in milliseconds
   */
  calculateDelay(attempt: number, config: RetryConfig): number;

  /**
   * Updates internal metrics with data from a retry attempt.
   *
   * @param attempt - The retry attempt record
   */
  updateMetrics(attempt: RetryAttempt): void;

  /**
   * Determines if retrying should continue based on context and configuration.
   *
   * @param context - The current request context
   * @returns True if another retry should be attempted
   */
  shouldContinueRetrying(context: RequestContext): boolean;

  /**
   * Gets the current retry metrics snapshot.
   *
   * @returns Current metrics snapshot
   */
  getRetryMetrics(): RetryMetricsSnapshot;

  /**
   * Resets all retry metrics to initial state.
   */
  resetMetrics(): void;

  /**
   * Adds a handler to receive retry notification events.
   *
   * @param handler - Function to call when events occur
   */
  addNotificationHandler(handler: (event: CoreNotificationEvent) => void): void;

  /**
   * Removes a previously added notification handler.
   *
   * @param handler - The handler function to remove
   */
  removeNotificationHandler(
    handler: (event: CoreNotificationEvent) => void
  ): void;
}

// =============================================================================
// DEFAULT CONFIGURATION
// =============================================================================

/**
 * Default retry configuration values.
 *
 * These defaults provide sensible starting values for most use cases:
 * - 3 retry attempts with exponential backoff
 * - Base delay of 1 second, max 10 seconds
 * - 10% jitter to prevent thundering herd
 * - Standard retryable status codes (timeouts, server errors)
 * - Safe HTTP methods only (excludes POST)
 * - Auth endpoints excluded from retry
 *
 * @example
 * ```typescript
 * // Use defaults
 * const engine = createRetryEngine(DEFAULT_RETRY_CONFIG);
 *
 * // Override specific values
 * const customConfig: RetryConfig = {
 *   ...DEFAULT_RETRY_CONFIG,
 *   maxAttempts: 5,
 *   baseDelay: 500,
 * };
 * ```
 */
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  exponentialBase: 2,
  jitterFactor: 0.1,
  enableCircuitBreaker: true,
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
  retryableMethods: ['GET', 'PUT', 'HEAD', 'OPTIONS', 'DELETE'],
  excludeEndpoints: ['/login', '/logout', '/register', '/api/user/auth'],
};

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard to check if a value is a valid RetryConfig.
 *
 * @param value - Value to check
 * @returns True if value is a valid RetryConfig
 *
 * @example
 * ```typescript
 * const config = getConfig();
 * if (isRetryConfig(config)) {
 *   engine.updateConfig(config);
 * }
 * ```
 */
export function isRetryConfig(value: unknown): value is RetryConfig {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const config = value as RetryConfig;

  return (
    typeof config.maxAttempts === 'number' &&
    typeof config.baseDelay === 'number' &&
    typeof config.maxDelay === 'number' &&
    typeof config.exponentialBase === 'number' &&
    typeof config.jitterFactor === 'number' &&
    typeof config.enableCircuitBreaker === 'boolean' &&
    Array.isArray(config.retryableStatusCodes) &&
    Array.isArray(config.retryableMethods) &&
    Array.isArray(config.excludeEndpoints)
  );
}

/**
 * Type guard to check if a value is a valid RetryAttempt.
 *
 * @param value - Value to check
 * @returns True if value is a valid RetryAttempt
 */
export function isRetryAttempt(value: unknown): value is RetryAttempt {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const attempt = value as RetryAttempt;

  return (
    typeof attempt.attemptNumber === 'number' &&
    typeof attempt.delay === 'number' &&
    attempt.error instanceof Error &&
    typeof attempt.timestamp === 'number' &&
    typeof attempt.requestId === 'string'
  );
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Validates a retry configuration and returns any validation errors.
 *
 * @param config - The configuration to validate
 * @returns Object with valid flag and array of error messages
 *
 * @example
 * ```typescript
 * const result = validateRetryConfig(config);
 * if (!result.valid) {
 *   console.error('Invalid config:', result.errors);
 * }
 * ```
 */
export function validateRetryConfig(config: RetryConfig): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (config.maxAttempts < 0) {
    errors.push('maxAttempts must be >= 0');
  }

  if (config.baseDelay < 0) {
    errors.push('baseDelay must be >= 0');
  }

  if (config.maxDelay < config.baseDelay) {
    errors.push('maxDelay must be >= baseDelay');
  }

  if (config.exponentialBase <= 0) {
    errors.push('exponentialBase must be > 0');
  }

  if (config.jitterFactor < 0 || config.jitterFactor > 1) {
    errors.push('jitterFactor must be between 0 and 1');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Checks if a status code is retryable.
 *
 * @param statusCode - Status code to check
 * @param config - Retry config
 * @returns True if the status code is retryable, false otherwise
 */
export function isRetryableStatusCode(
  statusCode: number,
  config: RetryConfig
): boolean {
  return config.retryableStatusCodes.includes(statusCode);
}

/**
 * Checks if a method is retryable.
 *
 * @param method - Method to check
 * @param config - Retry config
 * @returns True if the method is retryable, false otherwise
 */
export function isRetryableMethod(
  method: string = 'GET',
  config: RetryConfig
): boolean {
  return config.retryableMethods.includes(method.toUpperCase());
}

/**
 * Checks if an endpoint is excluded.
 *
 * @param url - URL to check
 * @param config - Retry config
 * @returns True if the endpoint is excluded, false otherwise
 */
export function isExcludedEndpoint(
  url: string = '',
  config: RetryConfig
): boolean {
  return config.excludeEndpoints.some((endpoint) => url.includes(endpoint));
}

/**
 * Creates a partial retry config with sensible overrides.
 *
 * @param overrides - Properties to override from defaults
 * @returns Complete retry config with overrides applied
 *
 * @example
 * ```typescript
 * const config = createRetryConfig({
 *   maxAttempts: 5,
 *   baseDelay: 500,
 * });
 * ```
 */
export function createRetryConfig(
  overrides: Partial<RetryConfig> = {}
): RetryConfig {
  return {
    ...DEFAULT_RETRY_CONFIG,
    ...overrides,
  };
}
